import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { chatKeys, useCreateChatThread } from '../api';
import { type MessagesInfiniteData, seedEmptyMessagesData } from '../messages-cache';

export type UseChatThreadReturn = {
    readonly threadId: string | null;
    readonly isCreatingThread: boolean;
    readonly isFreshThread: boolean;
    readonly ensureThreadId: () => Promise<string>;
};

export const useChatThread = (): UseChatThreadReturn => {
    const queryClient = useQueryClient();
    const createThread = useCreateChatThread();

    const [threadId, setThreadId] = useState<string | null>(null);
    const freshThreadIdRef = useRef<string | null>(null);

    const ensureThreadId = useCallback(async (): Promise<string> => {
        if (!is.null(threadId)) return threadId;
        const thread = await createThread.mutateAsync();
        freshThreadIdRef.current = thread.id;
        queryClient.setQueryData<MessagesInfiniteData>(chatKeys.messages(thread.id), seedEmptyMessagesData());
        setThreadId(thread.id);
        return thread.id;
    }, [threadId, createThread, queryClient]);

    const isFreshThread = !is.null(threadId) && freshThreadIdRef.current === threadId;

    return {
        ensureThreadId,
        isCreatingThread: createThread.isPending,
        isFreshThread,
        threadId,
    };
};
